/**
 * The Italian «codice fiscale» check, on the field while typing (U10). The same rule as core-api's
 * `identity/domain/owner-details.ts`, which stays the one that decides (`TAX_CODE_INVALID`).
 */

// Positions 7-8, 10-11 and 13-15 hold digits, or the letters that replace them when two people
// would share a code (omocodia: L M N P Q R S T U V for 0-9).
const TAX_CODE_SHAPE =
  /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;

// The value of a character in an odd position (1st, 3rd, … 15th): '0' counts as 'A', and so on.
const ODD_POSITION_VALUES = [
  1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23,
];

function characterIndex(character: string): number {
  const code = character.charCodeAt(0);
  return code <= 57 ? code - 48 : code - 65;
}

export function taxCodeCheckCharacter(firstFifteen: string): string {
  let sum = 0;
  for (let position = 0; position < 15; position += 1) {
    const index = characterIndex(firstFifteen.charAt(position));
    sum += position % 2 === 0 ? (ODD_POSITION_VALUES[index] ?? 0) : index;
  }
  return String.fromCharCode(65 + (sum % 26));
}

export type TaxCodeProblem = 'length' | 'shape' | 'check';

/** Why `value` (upper case) is not a valid tax code, or `null` when it is. */
export function taxCodeProblem(value: string): TaxCodeProblem | null {
  if (value.length !== 16) {
    return 'length';
  }
  if (!TAX_CODE_SHAPE.test(value)) {
    return 'shape';
  }
  return taxCodeCheckCharacter(value.slice(0, 15)) === value.charAt(15) ? null : 'check';
}
